import React from 'react';
import { Lock, Check } from 'lucide-react';
import { canAddGrowth } from '../zapGoalTemplates';

// unDraw illustration, recoloured to the Zap purple. Licence and provenance:
// src/assets/images/zap-goals/SOURCE.txt
import growthArt from '../../../../../../assets/images/zap-goals/goal-followers.svg';

/**
 * Growing followers, offered once and applied to whatever the Zap already does.
 *
 * It used to be a sixth card in the goal grid, which read as an alternative to
 * selling or collecting leads rather than something you do alongside them — a
 * creator who wanted both had to choose. Asking here instead lets any goal carry
 * a follow ask, and skipping leaves that goal exactly as it was.
 *
 * Only the answer lives here. How firm the ask is, and the wording of the card
 * that makes it, are later steps — and they exist only if this one says yes.
 */

interface StepGrowthProps {
  followGate: 'off' | 'soft' | 'hard';
  setFollowGate: (value: 'off' | 'soft' | 'hard') => void;
  entitlements: any;
  /** Not on this plan -> subscription screen, like the Zap hub's locked cards. */
  handleGoToUpgrade: () => void;
}

export function StepGrowth({ followGate, setFollowGate, entitlements, handleGoToUpgrade }: StepGrowthProps) {
  const available = canAddGrowth(entitlements);
  const growthOn = followGate !== 'off';

  const options = [
    {
      on: true,
      title: 'Yes, grow my followers',
      copy: 'They are asked to follow you before your message arrives.',
      locked: !available,
    },
    {
      on: false,
      title: 'No, send to everyone',
      copy: 'Anyone who comments gets it, follower or not.',
      locked: false,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center rounded-xl bg-[#fafafa] py-3">
        <img src={growthArt} alt="" aria-hidden="true" className="h-[92px] max-w-full object-contain" />
      </div>

      <div>
        <h3 className="mb-2">Grow your followers with this Zap?</h3>
        <p className="text-sm text-gray-500">
          Works with whatever you picked — people are asked to follow you first, then your
          message is sent.
        </p>
      </div>

      <div className="space-y-2">
        {options.map((option) => {
          const selected = option.on === growthOn;
          return (
            <div
              key={option.title}
              role="button"
              onClick={() => {
                if (option.locked) { handleGoToUpgrade(); return; }
                // 'hard' is the point of growing; the next step can soften it.
                setFollowGate(option.on ? 'hard' : 'off');
              }}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all ${
                selected ? 'border-[#7c3aed] bg-[#f5f3ff]' : 'border-gray-200 bg-white hover:border-purple-300'
              } ${option.locked ? 'opacity-70' : ''}`}
            >
              <span
                className={`mt-[2px] flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                  selected ? 'border-[#7c3aed]' : 'border-gray-300'
                }`}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-[#7c3aed]" />}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="mb-0 text-[14px] font-semibold text-[#111]">{option.title}</p>
                  {option.locked && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[1px] text-[10px] font-semibold text-[#7c3aed]">
                      <Lock className="h-[10px] w-[10px]" /> Upgrade
                    </span>
                  )}
                </div>
                <p className="mb-0 mt-0.5 text-[12px] text-gray-500">{option.copy}</p>
              </div>
            </div>
          );
        })}
      </div>

      {growthOn && (
        <div className="flex items-start gap-2 rounded-lg bg-[#f5f3ff] px-3 py-2">
          <Check className="mt-[2px] h-3.5 w-3.5 flex-shrink-0 text-[#7c3aed]" />
          <p className="mb-0 text-[12px] text-[#5b21b6]">
            You will choose how firmly to ask, and what that card says, in a moment.
          </p>
        </div>
      )}
    </div>
  );
}

export default StepGrowth;
